import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, Divider, Chip } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer from "../../MainDrawer"; // Adjust path if needed
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';

// --- Interfaces for Fine-Tuning Job Data ---
interface FineTuningOwner {
    id: string;
    name: string;
}

interface CostLabelPair {
    cost: string;
    label: string;
}

interface FineTuningDetailsData {
    baseModel: string;
    tuningMethod: string;
    trainingDataPath: string;
    validationDataPath: string;
    dataFormatTemplate: string;
    trainingDatasetId: string;
    validationDatasetId: string;
    trainingRegion: string;
    acceleratorType: string;
    acceleratorCount: string;
    epochs: string;
    learningRate: string;
    batchSize: string;
    loraRank: string;
    loraAlpha: string;
}

interface FineTuningApplication {
    id: string;
    applicationName: string;
}

interface FineTuningJobData {
    id: string;
    name: string;
    solutionType: string;
    cloudProvider: string;
    application: FineTuningApplication;
    costLabelPairs: CostLabelPair[];
    status: string;
    details: {
        fineTuning: FineTuningDetailsData;
    };
    solutionOwner: FineTuningOwner;
}

const FineTuningDetailsPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [fineTuningJob, setFineTuningJob] = useState<FineTuningJobData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchFineTuningJobData = async () => {
            if (!id) {
                setIsLoading(false);
                setError("Fine Tuning Job ID is missing from URL.");
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetFineTuning($getFineTuningId: ID!) {
                      getFineTuning(id: $getFineTuningId) {
                        id
                        name
                        solutionType
                        cloudProvider
                        application {
                          id
                          applicationName
                        }
                        costLabelPairs {
                          cost
                          label
                        }
                        status
                        details {
                          fineTuning {
                            baseModel
                            tuningMethod
                            trainingDataPath
                            validationDataPath
                            dataFormatTemplate
                            trainingDatasetId
                            validationDatasetId
                            trainingRegion
                            acceleratorType
                            acceleratorCount
                            epochs
                            learningRate
                            batchSize
                            loraRank
                            loraAlpha
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getFineTuningId: id });
                console.log("data", response.getFineTuning);
                if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching fine-tuning job data.");
                    setFineTuningJob(null);
                } else if (response && response.getFineTuning) {
                    setFineTuningJob(response.getFineTuning);
                } else {
                    setError("Fine-tuning job not found or unexpected response structure.");
                    setFineTuningJob(null);
                }
            } catch (err: any) {
                console.error("Error fetching Fine Tuning Job data:", err);
                setError(err.message || "Failed to fetch fine-tuning job details.");
                setFineTuningJob(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFineTuningJobData();
    }, [id]);
    console.log("fineTuningJob", fineTuningJob);
    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading fine-tuning job details...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!fineTuningJob) {
        return <Typography sx={{p:2}}>No fine-tuning job data available. Ensure the ID is correct.</Typography>;
    }

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8"; // Default
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved" || lowerStatus === "success") {
            return "#12B76A10"; // Light green tint
        } else if (lowerStatus === "rejected" || lowerStatus === 'failed') {
            return "#F0443810"; // Light red tint
        } else if (lowerStatus === "pending" || lowerStatus === 'provisioning') {
            return "#FDB02210"; // Light yellow/orange tint
        }
        return "#F8F8F8"; // Default for other statuses
    };
    
    const jobDetails = fineTuningJob?.details?.fineTuning;

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/aisolutions/finetuning" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/aisolutions/finetuning'); }}> Fine Tuning Jobs </Link>
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Fine Tuning Job Details</Typography>
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/aisolutions/finetuning')}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Fine Tuning Job Details </Typography>
            </Box>

            <Box sx={{mx:2, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(fineTuningJob.status),
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {fineTuningJob.name}
                            </Typography>
                            {fineTuningJob.status === "pending" && (
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/aisolutions/finetuning/edit/${fineTuningJob.id}`)}>
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Job Owner: {fineTuningJob.solutionOwner?.name || 'N/A'}
                        </Typography>
                    </Box>
                </Box>

                {/* --- Main Details Section --- */}
                <Grid container spacing={3} sx={{p:3, pt: 2.5, pb: 1, mt:0 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Application</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{fineTuningJob.application?.applicationName || "N/A"}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{fineTuningJob.cloudProvider?.toUpperCase()}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Base Model</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.baseModel}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Tuning Method</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.tuningMethod}</Typography>
                    </Grid>
                </Grid>

                {/* --- Dataset Configuration Section --- */}
                <Box sx={{ mt: 0, p: 2.5, pt: 1.5, pb: 1 }}>
                     <Divider sx={{mb: 2.5}} />
                    <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                        Dataset Configuration
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4} md={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Training Data Path</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333', wordBreak: 'break-all' }}>{jobDetails?.trainingDataPath}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Validation Data Path</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333', wordBreak: 'break-all' }}>{jobDetails?.validationDataPath || "N/A"}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Data Format Template</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.dataFormatTemplate}</Typography>
                        </Grid>
                    </Grid>
                </Box>
                
                {/* --- Training Infrastructure Section --- */}
                 <Box sx={{ mt: 0, p: 2.5, pt: 1.5, pb: 1 }}>
                     <Divider sx={{mb: 2.5}} />
                    <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                        Training Infrastructure
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Training Region</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.trainingRegion}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Accelerator Type</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.acceleratorType}</Typography>
                        </Grid>
                         <Grid item xs={6} sm={3}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Accelerator Count</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.acceleratorCount}</Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* --- Hyperparameters Section --- */}
                <Box sx={{ mt: 0, p: 2.5, pt: 1.5, pb: 1 }}>
                     <Divider sx={{mb: 2.5}} />
                    <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                        Hyperparameters
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Epochs</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.epochs}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Learning Rate</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.learningRate}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Batch Size</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.batchSize}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>LoRA Rank</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.loraRank}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>LoRA Alpha</Typography>
                            <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{jobDetails?.loraAlpha}</Typography>
                        </Grid>
                    </Grid>
                </Box>
                
                {/* --- Cost Labels Section --- */}
                {/* {fineTuningJob.costLabelPairs && fineTuningJob.costLabelPairs.length > 0 && (
                    <Box sx={{ mt: 1.5, p: 2.5, pt: 1.5 }}>
                        <Divider sx={{mb: 2.5}} />
                        <Typography component="h3" sx={{ fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                            Labels
                        </Typography> 
                        <Box>
                            {fineTuningJob.costLabelPairs.map((pair, index) => (
                                <Chip key={index} label={`${pair.cost}: ${pair.label}`} sx={{ mr: 1, mb: 1, fontFamily: 'Gilroy-Regular' }} />
                            ))}
                        </Box>
                    </Box>
                )} */}
            </Box>
        </Container>
    );
};

const FineTuningDetailsPage: React.FC = () => {
    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"aisolutions-finetuning"} />
            <MiniBot/>
            <Box component="main" sx={{ flexGrow: 1, p: 0, bgcolor: '#fafafa' }}>
                <FineTuningDetailsPageContent />
            </Box>
        </Box>
    );
};

export default FineTuningDetailsPage;